<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import DurationInput from 'dashboard/components-next/polaris/cards/DurationInput.vue';
import {
  Field,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';
import validations from './validations';

// The new SLA, in the kit's EditSheet (phase 9.3, D-G6/Q1, R2-9-5): the woot's fields (name, description, three
// times, business hours), its validation and its store action, in sections that fold. The times use the kit's
// DurationInput (whole numbers: 90 minutes rather than 1.5 hours).
const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('sla/getUIFlags');

const UNIT_SECONDS = { Minutes: 60, Hours: 3600, Days: 86400 };
const emptyTimes = () => [
  { value: null, unit: 'Minutes' },
  { value: null, unit: 'Minutes' },
  { value: null, unit: 'Minutes' },
];
const timeLabels = computed(() => [
  t('SLA.FORM.FIRST_RESPONSE_TIME.LABEL'),
  t('SLA.FORM.NEXT_RESPONSE_TIME.LABEL'),
  t('SLA.FORM.RESOLUTION_TIME.LABEL'),
]);

const name = ref('');
const description = ref('');
const times = ref(emptyTimes());
const onlyDuringBusinessHours = ref(false);

const v$ = useVuelidate({ name: validations.name }, { name });

const reset = () => {
  name.value = '';
  description.value = '';
  times.value = emptyTimes();
  onlyDuringBusinessHours.value = false;
  v$.value.$reset();
};
watch(open, isOpen => {
  if (isOpen) reset();
});

const dirty = computed(
  () =>
    !!name.value ||
    !!description.value ||
    onlyDuringBusinessHours.value ||
    times.value.some(time => time.value)
);

const nameError = computed(() => {
  if (!v$.value.name.$error) return '';
  return v$.value.name.required.$invalid
    ? t('SLA.FORM.NAME.REQUIRED_ERROR')
    : t('SLA.FORM.NAME.MINIMUM_LENGTH_ERROR');
});

const toSeconds = ({ value, unit }) =>
  value ? Number(value * (UNIT_SECONDS[unit] || 1)) : null;

const save = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  const [first, next, resolution] = times.value.map(toSeconds);
  try {
    await store.dispatch('sla/create', {
      name: name.value,
      description: description.value,
      first_response_time_threshold: first,
      next_response_time_threshold: next,
      resolution_time_threshold: resolution,
      only_during_business_hours: onlyDuringBusinessHours.value,
    });
    useAlert(t('SLA.ADD.API.SUCCESS_MESSAGE'));
    open.value = false;
  } catch (error) {
    useAlert(error.message || t('SLA.ADD.API.ERROR_MESSAGE'));
  }
};

const scope = computed(() => `${t('SIDEBAR.SETTINGS')} · ${t('SLA.HEADER')}`);
</script>

<template>
  <EditSheet
    v-model:open="open"
    :title="t('SLA.ADD.TITLE')"
    :scope="scope"
    :dirty="dirty"
    :save-label="t('SLA.FORM.CREATE')"
    :can-save="!uiFlags.isUpdating"
    @save="save"
  >
    <p class="type-polaris-body text-polaris-muted-foreground">
      {{ t('SLA.ADD.DESC') }}
    </p>
    <FoldingGroup :title="t('SETTINGS_PIECES.SECTIONS.DETAILS')">
      <div class="grid gap-4">
        <Field :data-invalid="!!nameError || undefined">
          <FieldLabel for="sla-name">{{ t('SLA.FORM.NAME.LABEL') }}</FieldLabel>
          <Input
            id="sla-name"
            v-model="name"
            :placeholder="t('SLA.FORM.NAME.PLACEHOLDER')"
            :aria-invalid="!!nameError || undefined"
            @blur="v$.name.$touch"
          />
          <FieldError v-if="nameError">{{ nameError }}</FieldError>
        </Field>
        <Field>
          <FieldLabel for="sla-description">
            {{ t('SLA.FORM.DESCRIPTION.LABEL') }}
          </FieldLabel>
          <Input
            id="sla-description"
            v-model="description"
            :placeholder="t('SLA.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </Field>
      </div>
    </FoldingGroup>
    <FoldingGroup :title="t('SETTINGS_PIECES.SECTIONS.TIMES')">
      <div class="grid gap-4">
        <Field v-for="(label, index) in timeLabels" :key="label">
          <FieldLabel>{{ label }}</FieldLabel>
          <DurationInput v-model="times[index]" :label="label" />
        </Field>
      </div>
    </FoldingGroup>
    <FoldingGroup :title="t('SLA.FORM.BUSINESS_HOURS.LABEL')">
      <Field orientation="horizontal">
        <FieldLabel for="sla-business-hours" class="flex-1">
          {{ t('SLA.FORM.BUSINESS_HOURS.PLACEHOLDER') }}
        </FieldLabel>
        <Switch id="sla-business-hours" v-model="onlyDuringBusinessHours" />
      </Field>
    </FoldingGroup>
  </EditSheet>
</template>
